/**
 * PaymentAttentionList (DNKADM-0472) — every booking at the location whose payment needs
 * attention, across ALL dates, not just the Day or Week the Grid is showing. Shown above
 * the Grid while its "Payment needs attention" filter is on: a count, then the bookings
 * oldest first. Each row is a button that opens that booking's drawer (Check payment,
 * Close out) through `onSelect`, the same way `BookingsListDialog` rows do.
 */
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { PaymentAttentionReservationsPage, Reservation } from '@/api/reservations';
import { resolveCalendarMaps } from '@/theme/maps/calendar';
import { clockInZone, shortDateLabel } from '@/utils/clockTime';
import { scrubBrand } from '@/utils/whitelabel';
import { localDateValueOf } from './derive';

export interface PaymentAttentionListProps {
  /** The all-dates read; null while loading. */
  page: PaymentAttentionReservationsPage | null;
  status: 'loading' | 'ready' | 'error';
  timeZone: string | null;
  onRetry: () => void;
  onSelect: (reservation: Reservation) => void;
}

/** Why the row is here: a Take payment left in progress, or one raised for review. */
function reasonOf(r: Reservation): string {
  return r.paymentNeedsReview === true ? 'Needs review' : 'Payment in progress';
}

function whenLabel(r: Reservation, timeZone: string | null): string {
  const day = shortDateLabel(localDateValueOf(r.startTime, timeZone));
  const time = clockInZone(r.startTime, timeZone);
  return [day, time].filter(Boolean).join(' · ');
}

function countLabel(total: number): string {
  return total === 1 ? '1 booking' : `${total} bookings`;
}

export function PaymentAttentionList({
  page,
  status,
  timeZone,
  onRetry,
  onSelect,
}: PaymentAttentionListProps) {
  const cm = resolveCalendarMaps(useTheme());

  if (status === 'error') {
    return (
      <Alert
        severity="error"
        sx={{ mb: 1.5 }}
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        The bookings that need payment attention could not be loaded.
      </Alert>
    );
  }
  if (status === 'loading' || page === null) return null;

  // Re-checked here like every other read in this dir, so a server that ignores the
  // param lists nothing rather than everything.
  const rows = page.content.filter(
    (r) => r.paymentStranded === true || r.paymentNeedsReview === true,
  );
  const total = rows.length === page.content.length ? page.totalElements : rows.length;

  return (
    <Box
      component="section"
      aria-label="Payment needs attention, all dates"
      sx={{ mb: 1.5, border: `1px solid ${cm.rowBorder}`, borderRadius: '10px', p: 1.5 }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: cm.textPrimary }}>
        {`Payment needs attention, all dates: ${countLabel(total)}`}
      </Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: cm.textMuted, mt: 0.5 }}>
          No booking has a payment that needs attention.
        </Typography>
      ) : (
        <Stack sx={{ gap: 0.75, mt: 1 }}>
          {rows.map((r) => (
            <Box
              key={r.reservationUuid ?? `${r.startTime}-${r.contactName}`}
              component="button"
              type="button"
              onClick={() => onSelect(r)}
              aria-label={`Open ${r.contactName ?? r.serviceName ?? 'reservation'} ${whenLabel(r, timeZone)}`}
              sx={{
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                minHeight: 52,
                px: 1.5,
                py: 1,
                border: `1px solid ${cm.rowBorder}`,
                borderRadius: '10px',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: cm.rowHoverBg },
                '&:active': { bgcolor: cm.rowHoverBg },
              }}
            >
              <Typography
                variant="mono"
                sx={{ fontSize: 12.5, color: cm.textMeta, width: 150, flexShrink: 0 }}
              >
                {whenLabel(r, timeZone)}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: cm.textPrimary }} noWrap>
                  {scrubBrand(r.contactName ?? 'Reservation')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: cm.textMuted }} noWrap>
                  {scrubBrand(r.serviceName ?? '')}
                </Typography>
              </Box>
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  px: '8px',
                  py: '3px',
                  borderRadius: '999px',
                  color: cm.statusPill.pending.fg,
                  bgcolor: cm.statusPill.pending.bg,
                  flexShrink: 0,
                }}
              >
                {reasonOf(r)}
              </Box>
            </Box>
          ))}
          {total > rows.length && (
            <Typography sx={{ fontSize: 12.5, color: cm.textMuted }}>
              {`Showing the oldest ${rows.length} of ${total}.`}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
